"use strict";
/**
 * CatalogItem Constants
 *
 * Enums and constants for catalog items (products/services from QVET).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CATALOG_ITEM_IMAGE_MAX_BYTES = exports.SYNC_STATUS_LABELS = exports.SYNC_STATUS_VALUES = exports.SyncStatus = exports.VAT_RATE_LABELS = exports.VAT_RATES = exports.IVA_TYPE_LABELS = exports.IVA_TYPE_VALUES = exports.IvaType = exports.FACTURAMA_SYNC_STATUS_LABELS = exports.FACTURAMA_SYNC_STATUS_VALUES = exports.FacturamaSyncStatus = exports.EDITABLE_STOCK_POLICIES = exports.STOCK_POLICY_LABELS = exports.STOCK_POLICY_VALUES = exports.StockPolicy = exports.USAGE_TYPE_LABELS = exports.USAGE_TYPE_VALUES = exports.UsageType = void 0;
exports.isValidVatRate = isValidVatRate;
exports.readableVatRates = readableVatRates;
/**
 * UsageType - How the item is used in HVP
 *
 * This is an HVP-only field (not in QVET).
 */
var UsageType;
(function (UsageType) {
    /** Direct sale to customer */
    UsageType["DIRECT_SALE"] = "directSale";
    /** Optional sale (may or may not be charged) */
    UsageType["OPTIONAL_SALE"] = "optionalSale";
    /** Auto-deducted from inventory when another item is sold (escandallos) */
    UsageType["AUTO_DEDUCT"] = "autoDeduct";
    /** Internal consumption (cleaning supplies, etc.) */
    UsageType["INTERNAL_USE"] = "internalUse";
    /** Fixed asset (equipment, furniture) */
    UsageType["FIXED_ASSET"] = "fixedAsset";
    /** Not applicable (e.g., external services) */
    UsageType["NOT_APPLICABLE"] = "notApplicable";
})(UsageType || (exports.UsageType = UsageType = {}));
/**
 * All UsageType values as array (for dropdowns)
 */
exports.USAGE_TYPE_VALUES = Object.values(UsageType);
/**
 * UsageType labels in Spanish (for UI)
 */
exports.USAGE_TYPE_LABELS = {
    [UsageType.DIRECT_SALE]: 'Venta Directa',
    [UsageType.OPTIONAL_SALE]: 'Venta Opcional',
    [UsageType.AUTO_DEDUCT]: 'Solo Escandallos',
    [UsageType.INTERNAL_USE]: 'Consumo Interno',
    [UsageType.FIXED_ASSET]: 'Activo Fijo',
    [UsageType.NOT_APPLICABLE]: 'No Aplica',
};
/**
 * StockPolicy - How stock levels are managed for an item
 *
 * This is an HVP-only field (not in QVET).
 * Determined by QVET's tipoControlStock field on sync.
 */
var StockPolicy;
(function (StockPolicy) {
    /** Stock min/max calculated based on consumption history */
    StockPolicy["DEMAND_BASED"] = "demandBased";
    /** Only ordered when customer requests, min/max = 0 but system proposes values */
    StockPolicy["ON_DEMAND"] = "onDemand";
    /** Item withdrawn from sale (discontinued, replaced, etc.), min/max = 0, no proposals */
    StockPolicy["DELISTED"] = "delisted";
    /** User defines stock levels manually, system does not calculate */
    StockPolicy["MANUAL"] = "manual";
    /** No stock control (services, etc.), field not editable */
    StockPolicy["NOT_APPLICABLE"] = "notApplicable";
})(StockPolicy || (exports.StockPolicy = StockPolicy = {}));
/**
 * All StockPolicy values as array (for dropdowns)
 */
exports.STOCK_POLICY_VALUES = Object.values(StockPolicy);
/**
 * StockPolicy labels in Spanish (for UI)
 */
exports.STOCK_POLICY_LABELS = {
    [StockPolicy.DEMAND_BASED]: 'Basado en Demanda',
    [StockPolicy.ON_DEMAND]: 'Bajo Pedido',
    [StockPolicy.DELISTED]: 'De Baja',
    [StockPolicy.MANUAL]: 'Manual',
    [StockPolicy.NOT_APPLICABLE]: 'No Aplica',
};
/**
 * Editable stock policies (for UI validation)
 * notApplicable is not editable - it's set automatically based on tipoControlStock
 */
exports.EDITABLE_STOCK_POLICIES = [
    StockPolicy.DEMAND_BASED,
    StockPolicy.ON_DEMAND,
    StockPolicy.DELISTED,
    StockPolicy.MANUAL,
];
/*
 * ═══════════════════════════════════════════════════════════════════════════════
 * Aquí vivían `ArticleType`, `TariffType` y `Brand`. Se borraron el 2026-09-07
 * (GH527 · SP3) por una razón medida: **no los leía nadie**.
 *
 * Los tres describían listas que QVET ya mantiene, y ninguno tenía un solo consumidor
 * en backend ni en frontend — `BRAND_VALUES` estaba anotado «for dropdowns» y no lo
 * usaba ningún desplegable. Un catálogo que nadie lee no se mantiene: se pudre.
 * `Brand` declaraba 37 marcas cuando los artículos tienen 49 y QVET 87, y decía
 * `ALPHACEM` donde todo lo demás dice `ALPHACHEM` — **cero coincidencias en la base**.
 *
 * Si vuelve a hacer falta una de estas listas, sale de `qvet_catalogs`, que las
 * sincroniza desde `POST /Parametros/Read`:
 *   Marca → `catalog: 'Marca'` · 87 entradas
 *   Tarifas → `catalog: 'Tarifas'` · 3, con su nombre real, que QVET renombra
 *
 * **No confundir con lo que NO se borró:** `IvaType` (más abajo) es el tratamiento
 * fiscal del CFDI, no la tasa de QVET; y `CatalogUnit` es nuestra lista para cerrar un
 * campo que QVET deja libre. Ninguno de los dos duplica un catálogo suyo.
 * ═══════════════════════════════════════════════════════════════════════════════
 */
/**
 * FacturamaSyncStatus - Status of sync with Facturama Product API
 */
var FacturamaSyncStatus;
(function (FacturamaSyncStatus) {
    /** Never synced to Facturama */
    FacturamaSyncStatus["NOT_SYNCED"] = "not_synced";
    /** Successfully synced */
    FacturamaSyncStatus["SYNCED"] = "synced";
    /** Changes made since last sync */
    FacturamaSyncStatus["PENDING"] = "pending";
    /** Last sync attempt failed */
    FacturamaSyncStatus["ERROR"] = "error";
})(FacturamaSyncStatus || (exports.FacturamaSyncStatus = FacturamaSyncStatus = {}));
exports.FACTURAMA_SYNC_STATUS_VALUES = Object.values(FacturamaSyncStatus);
exports.FACTURAMA_SYNC_STATUS_LABELS = {
    [FacturamaSyncStatus.NOT_SYNCED]: 'No sincronizado',
    [FacturamaSyncStatus.SYNCED]: 'Sincronizado',
    [FacturamaSyncStatus.PENDING]: 'Pendiente',
    [FacturamaSyncStatus.ERROR]: 'Error',
};
/**
 * IvaType - IVA treatment of a CFDI line: exempt, zero-rated or 16 %.
 *
 * **It is derived, never stored on the article** (GH581, 2026-09-24). QVET owns it through the
 * IVA type of the article and of each sale line (`16`, `0`, `EXENTO`); the app used to keep a
 * manual copy in `hvpData.ivaType` that contradicted QVET and was removed.
 */
var IvaType;
(function (IvaType) {
    /** IVA Tasa 0% (registered medicines) */
    IvaType["TASA_0"] = "tasa_0";
    /** IVA Exento (professional services) */
    IvaType["EXENTO"] = "exento";
    /** IVA Tasa 16% (standard rate) */
    IvaType["TASA_16"] = "tasa_16";
})(IvaType || (exports.IvaType = IvaType = {}));
exports.IVA_TYPE_VALUES = Object.values(IvaType);
exports.IVA_TYPE_LABELS = {
    [IvaType.TASA_0]: 'Tasa 0%',
    [IvaType.EXENTO]: 'Exento',
    [IvaType.TASA_16]: 'Tasa 16%',
};
/**
 * VAT rates a catalog item may carry, as a **percentage** — which is how QVET stores it
 * (`IVA` / `IVACompra`) and how the catalog form shows it.
 *
 * Not to be confused with {@link IvaType}, which is the *fiscal treatment* that travels to
 * the CFDI. `exento` is a treatment, not a rate: an exempt article carries **0** here and
 * `exento` there. QVET has no way to express the difference — its catalogue is
 * percentages — so the app keeps the two apart on purpose.
 *
 * **Why this is a fixed list and not read from QVET.** Mexico's rates are national and
 * effectively static; a change would be a legislative event, not a Tuesday. QVET's own
 * catalogue, by contrast, had a 6 % and a 7 % in it — leftovers nobody could explain, in
 * use by nine deactivated articles, retired on 2026-09-05 (GH527). Reading the list from
 * QVET would have offered that junk back to the user. Keeping it here means the app states
 * what is valid and QVET's mess cannot leak into the form.
 *
 * **8 % is deliberately absent.** It exists, but only for the northern and southern border
 * strips; Mérida is in neither.
 */
exports.VAT_RATES = [0, 16];
exports.VAT_RATE_LABELS = {
    0: '0 %',
    16: '16 %',
};
/** Accepts the number or its string form — form inputs hand over strings. */
function isValidVatRate(value) {
    const n = typeof value === 'string' ? Number(value.replace('%', '').trim()) : Number(value);
    return Number.isFinite(n) && exports.VAT_RATES.includes(n);
}
/** «0 % y 16 %», for error messages. */
function readableVatRates() {
    const list = exports.VAT_RATES.map((r) => exports.VAT_RATE_LABELS[r]);
    return list.length > 1 ? `${list.slice(0, -1).join(', ')} y ${list[list.length - 1]}` : list.join('');
}
/**
 * SyncStatus - Status of sync with QVET
 */
var SyncStatus;
(function (SyncStatus) {
    /** Fully synced with QVET, no pending changes */
    SyncStatus["SYNCED"] = "synced";
    /** Has local changes pending upload to QVET */
    SyncStatus["PENDING"] = "pending";
    /** Local changes conflict with QVET changes */
    SyncStatus["CONFLICT"] = "conflict";
    /** Item exists in HVP but not found in last QVET sync */
    SyncStatus["MISSING_FROM_QVET"] = "missing_from_qvet";
})(SyncStatus || (exports.SyncStatus = SyncStatus = {}));
/**
 * All SyncStatus values as array
 */
exports.SYNC_STATUS_VALUES = Object.values(SyncStatus);
/**
 * SyncStatus labels in Spanish (for UI)
 */
exports.SYNC_STATUS_LABELS = {
    [SyncStatus.SYNCED]: 'Sincronizado',
    [SyncStatus.PENDING]: 'Pendiente',
    [SyncStatus.CONFLICT]: 'Conflicto',
    [SyncStatus.MISSING_FROM_QVET]: 'No encontrado en QVET',
};
/**
 * Largest photo accepted for a catalog item, in bytes (8 MB). The backend rejects anything bigger and the frontend
 * stops it before uploading. Generous on purpose: the largest image in the catalog weighs 457 KB.
 */
exports.CATALOG_ITEM_IMAGE_MAX_BYTES = 8 * 1024 * 1024;
